/**
 * Color scale utilities for OD analysis visualization
 * Scale: Light cream -> Yellow -> Orange -> Red (matching reference image)
 */

// Color stops based on the reference image (from low to high)
const colorStops = [
  { percent: 0, color: '#FFFFCC' },    // rgb(255, 255, 204) - Light yellow
  { percent: 20, color: '#FFE692' },   // rgb(255, 230, 146) - Yellow
  { percent: 40, color: '#FEBF5A' },   // rgb(254, 191, 90) - Light orange
  { percent: 60, color: '#FD8D3C' },   // rgb(253, 141, 60) - Orange
  { percent: 80, color: '#F43D25' },   // rgb(244, 61, 37) - Red-orange
  { percent: 100, color: '#CA0923' },  // rgb(202, 9, 35) - Dark red
];

/**
 * Interpolate between two colors
 */
const interpolateColor = (color1, color2, factor) => {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');

  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);

  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);

  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Get color for a given percentage value
 * @param {number} percent - Value between 0 and 100
 * @returns {string} RGB color string
 */
export const getColorForPercentage = (percent) => {
  // Clamp percentage between 0 and 100
  const clampedPercent = Math.max(0, Math.min(100, percent));

  // Find the two color stops to interpolate between
  let lowerStop = colorStops[0];
  let upperStop = colorStops[colorStops.length - 1];

  for (let i = 0; i < colorStops.length - 1; i++) {
    if (clampedPercent >= colorStops[i].percent && clampedPercent <= colorStops[i + 1].percent) {
      lowerStop = colorStops[i];
      upperStop = colorStops[i + 1];
      break;
    }
  }

  // Calculate interpolation factor
  const range = upperStop.percent - lowerStop.percent;
  const factor = range === 0 ? 0 : (clampedPercent - lowerStop.percent) / range;

  return interpolateColor(lowerStop.color, upperStop.color, factor);
};

/**
 * Get hex color for a given percentage value
 * @param {number} percent - Value between 0 and 100
 * @returns {string} Hex color string
 */
export const getHexColorForPercentage = (percent) => {
  const clampedPercent = Math.max(0, Math.min(100, percent));

  // Find the two color stops to interpolate between
  let lowerStop = colorStops[0];
  let upperStop = colorStops[colorStops.length - 1];

  for (let i = 0; i < colorStops.length - 1; i++) {
    if (clampedPercent >= colorStops[i].percent && clampedPercent <= colorStops[i + 1].percent) {
      lowerStop = colorStops[i];
      upperStop = colorStops[i + 1];
      break;
    }
  }

  // For simplicity, return the lower stop color
  const range = upperStop.percent - lowerStop.percent;
  const factor = range === 0 ? 0 : (clampedPercent - lowerStop.percent) / range;

  // Return interpolated hex color
  const hex1 = lowerStop.color.replace('#', '');
  const hex2 = upperStop.color.replace('#', '');

  const r1 = parseInt(hex1.substring(0, 2), 16);
  const g1 = parseInt(hex1.substring(2, 4), 16);
  const b1 = parseInt(hex1.substring(4, 6), 16);

  const r2 = parseInt(hex2.substring(0, 2), 16);
  const g2 = parseInt(hex2.substring(2, 4), 16);
  const b2 = parseInt(hex2.substring(4, 6), 16);

  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Calculate percentage based on trips value and maximum
 * @param {number} trips - Number of trips
 * @param {number} maxTrips - Maximum number of trips
 * @returns {number} Percentage value (0-100)
 */
export const calculatePercentage = (trips, maxTrips) => {
  if (!maxTrips || maxTrips === 0) return 0;
  return (trips / maxTrips) * 100;
};

/**
 * Get the legend color stops for display
 * @returns {Array} Array of { label, color } objects
 */
export const getLegendStops = () => {
  return [
    { label: '0% - 19%', color: colorStops[0].color, percent: 0 },
    { label: '20% - 39%', color: colorStops[1].color, percent: 20 },
    { label: '40% - 59%', color: colorStops[2].color, percent: 40 },
    { label: '60% - 79%', color: colorStops[3].color, percent: 60 },
    { label: '80% - 89%', color: colorStops[4].color, percent: 80 },
    { label: '90% - 100%', color: colorStops[5].color, percent: 100 },
  ];
};

/**
 * Default zone style (no selection)
 */
export const defaultZoneStyle = {
  fillColor: '#d3d3d3',
  color: '#808080',
  weight: 2,
  opacity: 1,
  fillOpacity: 0.1,
};

/**
 * Get zone style based on trips data
 * @param {number} trips - Number of trips for this zone
 * @param {number} maxTrips - Maximum trips across all zones
 * @param {boolean} isOrigin - Whether this zone is the selected origin
 * @param {boolean} isDestination - Whether this zone is the selected destination
 * @returns {object} Leaflet path style object with percentage
 */
export const getZoneStyle = (trips, maxTrips, isOrigin, isDestination) => {
  // Calculate color based on percentage
  const percent = calculatePercentage(trips, maxTrips);
  const fillColor = getColorForPercentage(percent);

  // Selected origin - gray fill with green border (origin is starting point, no coloring)
  if (isOrigin) {
    return {
      ...defaultZoneStyle,
      color: '#2E7D32',
      weight: 3,
      percent: 0,
    };
  }

  // Selected destination - colored based on trips with blue border
  if (isDestination) {
    return {
      fillColor: trips > 0 ? fillColor : defaultZoneStyle.fillColor,
      color: '#1565C0',
      weight: 3,
      opacity: 1,
      fillOpacity: 0.7,
      percent,
    };
  }

  // If no trips data, use default gray
  if (trips === undefined || trips === null || trips === 0) {
    return { ...defaultZoneStyle, percent: 0 };
  }

  // Base style with value-based color for zones with trips
  return {
    fillColor,
    color: '#404040',
    weight: 2,
    opacity: 1,
    fillOpacity: 0.7,
    percent,
  };
};
